<template>
  <v-app>
    <v-alert
      v-if="showBanner"
      type="info"
      prominent
      dense
      dismissible
      class="site-banner mb-0"
      style="border-radius: 0; z-index: 100;"
      @input="dismissBanner"
    >
      <span class="font-weight-medium">{{ bannerMessage }}</span>
    </v-alert>
    <MPopupBalloon />
    <component :is="layout" v-if="layout">
      <router-view />
    </component>
    <div v-else class="app-boot" aria-hidden="true" />
    <ai-feedback-cursor />
    <!-- AIPM Retroactive Onboarding Dialog -->
    <UnifiedAuthDialog />
    <AIPMOnboardingDialog
      v-if="deferredDialogs"
      @complete="handleRetroactiveOnboardingComplete"
      @dismiss="handleRetroactiveOnboardingDismiss"
    />
    <!-- AI Creator Shell - available on creator routes only -->
    <AiCreatorShell v-if="showAiCreatorShell" />
    <!-- L0/L1 Upgrade gate modal — singleton, opens whenever any flow throws a BillingError -->
    <UpgradeModal v-if="deferredDialogs" />
  </v-app>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  ref,
  getCurrentInstance,
  onMounted,
  watch,
  nextTick
} from 'vue';
import AIFeedbackCursor from '@/components/AIFeedbackCursor.vue';
import { useToolGetters, useUserGetters, useUserActions } from '@/store';
import { useAuthOrchestrator } from '@/composables/useAuthOrchestrator';
import { useProgramVisitTracker } from '@/composables/useProgramVisitTracker';
import { profileStorage } from '@/services/profileStorage';
import { useRetroactiveOnboarding } from '@/composables/useRetroactiveOnboarding';
import { useAIPMOnboarding, canStartOnboarding } from '@/composables/useAIPMOnboarding';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { normalizeAccessPolicy } from '@/types/accessPolicy';
import { buildProgramJoinContext, programPageTitle } from '@/types/programJoinContext';
import { isJoinPeekProgram } from '@/constants/joinPeek';
import { useFunnelTracker } from '@/composables/useFunnelTracker';
import { getAutoPilotByProgramId } from '@/api/outreachApi';
import '@/styles/main.scss';

export default defineComponent({
  name: 'App',
  components: {
    'ai-feedback-cursor': AIFeedbackCursor,
    MPopupBalloon: () => import('@/components/molecules/m-popup-balloon.vue'),
    UnifiedAuthDialog: () => import('@/components/auth/UnifiedAuthDialog.vue'),
    AIPMOnboardingDialog: () => import('@/components/organisms/AIPMOnboardingDialog.vue'),
    AiCreatorShell: () => import('@/components/organisms/AiCreatorShell.vue'),
    UpgradeModal: () => import('@/components/billing/UpgradeModal.vue')
  },
  setup(props, context: any) {
    const instance = getCurrentInstance();
    const { getUser, isDialogOpen, isProfileComplete } = useUserGetters([
      'getUser',
      'getObjectId',
      'isDialogOpen',
      'isProfileComplete'
    ]);
    const orchestrator = useAuthOrchestrator();
    // The retroactive-onboarding and upgrade dialogs are never on screen during first paint;
    // mounting them at boot downloaded ~40 KB of chunks (AIPM + Places + UpgradeModal) and ran
    // their setup inside the landing's critical window. They mount once the page has settled.
    const deferredDialogs = ref(false);
    onMounted(() => {
      const ready = () => {
        deferredDialogs.value = true;
      };
      const w = window as unknown as {
        requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
      };
      window.setTimeout(() => {
        if (w.requestIdleCallback) w.requestIdleCallback(ready, { timeout: 2000 });
        else ready();
      }, 3000);
    });

    // Funnel stages 3-5 (Track 6.1): read off the auth phase transitions, only while the
    // visitor is on a program guide (that is the funnel we measure).
    const { track: trackFunnel } = useFunnelTracker();
    const funnelProgramId = () => {
      const r = instance?.proxy?.$route;
      return r?.name === 'guide' || /^\/guide\//.test(r?.path || '')
        ? String(r?.params?.programId || '')
        : '';
    };
    watch(
      () => orchestrator.state.phase,
      (phase, prev) => {
        const programId = funnelProgramId();
        if (!programId || !phase || phase === prev) return;
        trackFunnel({ programId, event: 'auth_step', meta: { phase, prev: prev || null } });
        if (prev === 'contact' && (phase === 'otp' || phase === 'password')) {
          trackFunnel(
            {
              programId,
              event: 'auth_contact_submitted',
              meta: { method: orchestrator.state.isEmail ? 'email' : 'phone' }
            },
            { once: true }
          );
        }
        if (prev === 'otp' && phase !== 'contact' && phase !== 'error') {
          trackFunnel({ programId, event: 'auth_code_verified' }, { once: true });
        }
        if (phase === 'complete') {
          trackFunnel({ programId, event: 'auth_completed' }, { once: true });
        }
      }
    );
    const { getProgramByNameOrIdOrSharableUrl } = useUserActions([
      'getProgramByNameOrIdOrSharableUrl'
    ]);
    const { trackProgramVisit } = useProgramVisitTracker();
    const retroactiveOnboarding = useRetroactiveOnboarding();
    const aipmOnboarding = useAIPMOnboarding();

    // Track if we've already checked retroactive onboarding this session
    const hasCheckedRetroactiveOnboarding = ref(false);

    // Set default title only if no other title exists
    if (!document.title || document.title === '' || document.title.includes('localhost')) {
      document.title = 'PilotCity  |  Build projects to earn internships';
    }

    // Track if we've already auto-opened the dialog this session.
    // Uses sessionStorage so the flag survives hard reloads within the same tab
    // but resets when the tab is closed — prevents bootloop from window.location.href reloads.
    const SESSION_KEY = 'auth_dialog_auto_opened';
    const hasAutoOpenedDialog = ref(!!sessionStorage.getItem(SESSION_KEY));

    // ========================================================================
    // AIPM Retroactive Onboarding (Task Group 6.4)
    // ========================================================================
    // Triggers onboarding for returning participants who haven't completed it.
    // Different messaging than checkout flow: "We've been helping you behind the scenes..."
    // ========================================================================

    /**
     * Handle retroactive onboarding completion
     */
    const handleRetroactiveOnboardingComplete = () => {
      console.log('[App] Retroactive onboarding completed');
      // The onboarding composable handles the state update
    };

    /**
     * Handle retroactive onboarding dismissal
     */
    const handleRetroactiveOnboardingDismiss = () => {
      console.log('[App] Retroactive onboarding dismissed');
      // The retroactive onboarding composable records the dismissal
    };

    /**
     * Check if user is a participant in a program
     * This is a simplified check - in production this would query user's enrolled programs
     */
    const checkForParticipantPrograms = async (user: any): Promise<string | null> => {
      // Check the current route for program context
      const currentRoute = instance?.proxy?.$route;

      // Look for program ID in route params or query
      const programIdFromRoute =
        currentRoute?.params?.programId ||
        currentRoute?.query?.programId ||
        currentRoute?.params?.id;

      if (programIdFromRoute) {
        return programIdFromRoute as string;
      }

      // Could also check user's enrolled programs here
      // For now, we rely on route context
      return null;
    };

    // Watch for user becoming available with incomplete profile
    // This handles the async nature of auth - user data loads after mount
    watch(
      [getUser, isProfileComplete],
      async ([user, profileComplete]) => {
        // Skip if already handled or dialog is open
        if (hasAutoOpenedDialog.value || orchestrator.state.isOpen) return;

        const currentRoute = instance?.proxy?.$route;
        const isAuthRoute = currentRoute?.name === 'signup' || currentRoute?.name === 'login';
        // The PRA evidence-desk tool is fully self-gated (its own email-OTP session);
        // never overlay the host product's Auth0 dialog on top of it.
        const isPraRoute = currentRoute?.path?.startsWith('/pra') ?? false;
        if (isPraRoute) return;
        // /comms is the internal staff console (SMS inbox + audience builder).
        // Staff using it must not be force-routed through student onboarding.
        // NB: on a hard load the Vue router hasn't resolved yet (currentRoute is
        // still "/"), so also check window.location — otherwise the gate fires
        // before the route settles on /comms.
        const onCommsPath =
          (currentRoute?.path?.startsWith('/comms') ?? false) ||
          (typeof window !== 'undefined' && window.location.pathname.startsWith('/comms'));
        if (onCommsPath) return;
        // /oauth/consent is where hosted MCP connectors (claude.ai, ChatGPT) sign a user in and
        // ask for consent. The page runs its own sign-in and must finish on its own route: force-
        // opening onboarding here would strand the flow at /portfolio/my-programs and the
        // connector would never receive its code. Same hard-load caveat as /comms above.
        const onConsentPath =
          (currentRoute?.path?.startsWith('/oauth/consent') ?? false) ||
          (typeof window !== 'undefined' && window.location.pathname.startsWith('/oauth/consent'));
        if (onConsentPath) return;
        // /shiftspend has its own nurse-specific auth flow (LoginPage.vue) and
        // handles its own redirects; the main auth orchestrator must not auto-
        // open there or it'll route nurse users to /portfolio post-login.
        const isShiftSpendRoute = typeof currentRoute?.path === 'string' && currentRoute.path.startsWith('/shiftspend');

        // Auto-open auth dialog if:
        // 1. User is authenticated
        // 2. Profile is incomplete
        // 3. Not on an auth route
        // 4. Not on a /shiftspend route (it owns its own auth)
        if (user && !profileComplete && !isAuthRoute && !isShiftSpendRoute) {
          // `profileComplete` is a localStorage-backed flag, and the per-login
          // server query that refreshes it may not have resolved yet on a hard
          // refresh. Trusting the stale flag flashes the onboarding dialog open
          // for users who are actually complete — and once auto-opened, the
          // guard above prevents it from ever re-closing. So confirm against the
          // server before gating; only open if completeness is authoritatively
          // false. Reserve the guard first to prevent a re-entrant double-check.
          hasAutoOpenedDialog.value = true;
          try {
            const [{ default: nexusClient }, { IS_PORTFOLIO_COMPLETE_QUERY }, storeMod, mutationsMod] =
              await Promise.all([
                import('@/apollo/nexusClient'),
                import('@/services/graphql/nexus/auth'),
                import('@/store'),
                import('@/store/modules/user/mutations')
              ]);
            const { data } = await nexusClient.query<{ authIsPortfolioComplete: boolean }>({
              query: IS_PORTFOLIO_COMPLETE_QUERY,
              fetchPolicy: 'network-only'
            });
            if (data?.authIsPortfolioComplete === true) {
              // Stale-flag false alarm: sync the truth and don't open.
              localStorage.setItem('profileComplete', 'true');
              (storeMod.default as any)?.commit(
                `user/${mutationsMod.MutationTypes.SET_PROFILE_COMPLETE}`,
                true
              );
              hasAutoOpenedDialog.value = false;
              console.log('[App] Profile complete (server-confirmed) — not opening dialog');
              return;
            }
          } catch (confirmErr) {
            // Could NOT verify completeness — network/backend error or a
            // cold-boot token race where this fires before the auth header is
            // attached (symptom: the completeness query 401s/502s on hard
            // refresh). For an already-authenticated user, never fall through
            // to opening onboarding on an UNCONFIRMED result: a spurious open
            // immediately resolves to "Welcome Back" and redirects every route
            // to /portfolio. Fail safe — skip opening this tick and reset the
            // guard so a later successful check can still open if the profile
            // is genuinely incomplete. Only a positive server "false" opens.
            console.warn('[App] completeness re-check failed; skipping auto-open (fail-safe):', confirmErr);
            hasAutoOpenedDialog.value = false;
            return;
          }
          console.log('[App] Profile incomplete (server-confirmed), auto-opening auth dialog');
          sessionStorage.setItem(SESSION_KEY, 'true');
          orchestrator.openAuth();
          return;
        }

        // Check for recoverable profile session (for navbar banner)
        if (user && profileComplete && !isAuthRoute) {
          const session = profileStorage.getSession();
          if (session) {
            const incompletePhases = [
              'generalProfile',
              'teacherProfile',
              'studentProfile',
              'parentProfile',
              'employerProfile',
              'schoolProfile',
              'organizerProfile'
            ];
            if (incompletePhases.includes(session.phase)) {
              orchestrator.state.recoveryAvailable = true;
              console.log('[App] Profile recovery available for phase:', session.phase);
            }
          }
        }
      },
      { immediate: true }
    );

    // ========================================================================
    // Shared Program Link — Unauth In-Place Auth Modal
    // ========================================================================
    // When an unauthenticated visitor lands on /guide/:programId/:page (a shared
    // program link), keep them on that URL and force them through auth + portfolio
    // setup via a non-dismissible UnifiedAuthDialog overlay. After completion,
    // the existing `createStudent` auto-enrollment in ParticipantGuide fires and
    // they continue as a real participant on the same URL.
    //
    // Companion changes:
    //   - router/index.ts skips the redirect-to-landing for `to.name === 'guide'`
    //   - useAuthFlowControl.ts.closeAuth refuses to close while nondismissible=true
    //   - useAuthFlowControl.ts.completeAndRedirect resets nondismissible on success
    // ========================================================================
    // Per-pageload guard for the unauth /guide auto-open. We can't reuse
    // hasAutoOpenedDialog (it persists via sessionStorage and would block re-opens after
    // the visitor closes the modal or a stale flag survives a reload).
    const hasOpenedUnauthGuideDialog = ref(false);
    watch(
      [getUser, () => instance?.proxy?.$route?.name, () => instance?.proxy?.$route?.fullPath],
      async ([user, routeName, routeFullPath]: any) => {
        if (orchestrator.state.isOpen) return;
        if (user) return;
        if (routeName !== 'guide') return;
        if (hasOpenedUnauthGuideDialog.value) return;
        // Anon-first /guide: if this browser already holds an anon enrollment for
        // this program (joined via "Join now"), ParticipantGuide resolves the real
        // Student in place — do NOT reopen the join dialog over it.
        try {
          const anonPid = instance?.proxy?.$route?.params?.programId;
          if (anonPid && localStorage.getItem(`pc_anon_student:${anonPid}`)) return;
        } catch {
          /* localStorage unavailable — fall through to normal dialog */
        }

        hasOpenedUnauthGuideDialog.value = true;

        // Access-policy ladder on unauth /guide: when ACCESS_POLICY is on, resolve
        // the program's accessPolicy so the join dialog shows the correct rung over
        // the real ParticipantGuide (a public-program slug redirects into /guide via
        // Setup.vue). Mirrors Setup.vue's setAccessPolicy/setAuthMode ordering: set
        // BEFORE openAuth so ContactPhase sees the right rung with no flash.
        //   'on'  → full standard account flow
        //   else  → express name+contact panel ("Join now" / verified express)
        // Non-public programs or flag-off fall through to the unchanged full auth.
        // eslint-disable-next-line no-console
        console.warn('[AP-DBG][App:unauthGuide] dialog trigger', {
          route: routeName,
          fullPath: routeFullPath,
          accessPolicyFlag: FEATURE_FLAGS.ACCESS_POLICY
        });
        // Join peek (Track 6 experiment 2): on allowlisted, published, public programs
        // the box is dismissible so the visitor can look at the guide first; the
        // guide shows a sticky Join bar meanwhile (ParticipantGuide).
        let joinPeek = false;
        if (FEATURE_FLAGS.ACCESS_POLICY) {
          try {
            const programId = instance?.proxy?.$route?.params?.programId;
            if (programId) {
              const prog = await getProgramByNameOrIdOrSharableUrl({
                type: 'by_id',
                program_id: programId
              });
              // eslint-disable-next-line no-console
              console.warn('[AP-DBG][App:unauthGuide] resolved program', {
                programId,
                found: !!prog,
                isPublic: prog?.isPublic,
                rawAccessPolicy: prog?.accessPolicy
              });
              // Reach analytics: this watcher only runs for UNAUTHENTICATED
              // /guide visitors, so there's no creator self-view to exclude.
              // Fire-and-forget; the tracker self-guards (flag + once-per-load).
              if (prog) {
                void trackProgramVisit(programId);
                // B8: the sign-in box names the program (name, organizer, price,
                // time, credential) and the tab carries the program name.
                // Only for a program the visitor can actually join: an unpublished
                // program renders "This program isn't available" behind the dialog,
                // and a "You are joining ..." card on top of that reads as a lie
                // (local pass, 2026-09-13). Organizers still sign in from the same box.
                if (prog.published !== false) {
                  orchestrator.setProgramContext(buildProgramJoinContext(prog));
                  joinPeek = !!prog.isPublic && isJoinPeekProgram(programId);
                }
                document.title = programPageTitle(prog.programName);
              }
              if (prog?.isPublic) {
                const policy = normalizeAccessPolicy(prog.accessPolicy);
                orchestrator.setAccessPolicy(policy, programId);
                orchestrator.setAuthMode(policy.floor === 'on' ? 'standard' : 'express');
                // eslint-disable-next-line no-console
                console.warn('[AP-DBG][App:unauthGuide] → access-policy ladder', {
                  floor: policy.floor,
                  allowUpgrades: policy.allowUpgrades,
                  authMode: policy.floor === 'on' ? 'standard' : 'express'
                });
              } else {
                // eslint-disable-next-line no-console
                console.warn(
                  '[AP-DBG][App:unauthGuide] program not public → full auth fallback'
                );
              }
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(
              '[AP-DBG][App:unauthGuide] resolve failed → full auth fallback:',
              (e as Error)?.message
            );
          }
        }

        // Join peek: the guide itself is the landing (JoinPeekHero); the box opens on Join.
        if (!joinPeek) orchestrator.openAuth(routeFullPath, { nondismissible: true });
      },
      { immediate: true }
    );

    // Cleanup: handle the case where `user` hydrates while the persistent dialog is
    // still open. Two scenarios:
    //
    //   1. Silent token refresh (phase still 'idle' or 'contact'): visitor never
    //      interacted with the dialog. Release the lock and close cleanly — they're
    //      already authed, the modal is redundant.
    //
    //   2. Mid-auth-flow (phase 'otp' / 'password' / 'generalProfile' / 'studentProfile'
    //      / etc): visitor is actively progressing through signup + portfolio wizard.
    //      `user` may hydrate after OTP succeeds but BEFORE the wizard completes — if
    //      we release the lock here, the visitor can X out mid-wizard and end up with
    //      a Student doc created from an incomplete profile. Leave the lock on; the
    //      natural completeProfileAndRedirect path will release it on 'complete' phase.
    watch(getUser, user => {
      if (!user || !orchestrator.state.nondismissible) return;
      const phase = orchestrator.state.phase;
      const isSilentRefresh = phase === 'idle' || phase === 'contact';
      if (!isSilentRefresh) {
        // Wizard / OTP in progress — keep the lock; let completion path handle close.
        return;
      }
      orchestrator.releaseNondismissible();
      if (orchestrator.state.isOpen) {
        orchestrator.closeAuth();
      }
      // Silent refresh closed the modal, but ParticipantGuide's setup() already ran in
      // unauth mode with synthetic Student data. Force a reload so it remounts with the
      // authed user — the auto-enroll createStudent path in the authed branch will then
      // fire naturally. Mirrors the post-completion reload in completeAndRedirect.
      const currentRoute = instance?.proxy?.$route;
      if (currentRoute?.name === 'guide') {
        window.location.assign(currentRoute.fullPath);
      }
    });

    // Third-party guardian /sign return: when a guardian authenticates from the
    // /sign page, the orchestrator's internal redirect can drop the target (a fresh
    // signup's multi-step flow resets originalRedirect → they land on the landing
    // page). Agreement.vue stashed the /sign URL in localStorage; bring them back
    // with a full reload so it remounts authed and the agreement loads.
    watch(getUser, user => {
      if (!user) return;
      let signReturnUrl: string | null = null;
      try {
        signReturnUrl = localStorage.getItem('signReturnUrl');
      } catch (_) {
        /* private mode — non-fatal */
      }
      if (!signReturnUrl) return;
      try {
        localStorage.removeItem('signReturnUrl');
      } catch (_) {
        /* non-fatal */
      }
      // Already on the sign page (e.g. completeAndRedirect's reload landed us here)?
      // Then no navigation needed — Agreement.vue will load with the authed token.
      if (instance?.proxy?.$route?.fullPath !== signReturnUrl) {
        window.location.assign(signReturnUrl);
      }
    });

    // ========================================================================
    // AIPM Retroactive Onboarding Auto-Trigger (Task Group 6.4)
    // ========================================================================
    // Watch for authenticated users on program pages and trigger onboarding
    // if they haven't completed it yet and the 24h dismissal window has passed.
    // ========================================================================

    /**
     * Check if current route is a program-related page
     */
    const isProgramRoute = (route: any): boolean => {
      if (!route) return false;
      const programRoutes = ['experience', 'guide', 'program', 'project'];
      return programRoutes.some(r => route.name?.includes(r) || route.path?.includes(r));
    };

    /**
     * Extract program ID from current route
     */
    const getProgramIdFromRoute = (route: any): string | null => {
      if (!route) return null;
      return route.params?.programId || route.params?.id || route.query?.programId || null;
    };

    /**
     * Fetch onboarding status from backend
     */
    const fetchOnboardingStatus = async (
      programId: string
    ): Promise<{
      funnelState: 'not_started' | 'in_progress' | 'complete';
      completedAt?: string;
    } | null> => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_NEXUS_API || 'http://localhost:3007'}/graphql`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
            },
            body: JSON.stringify({
              query: `
              query AIPMOnboardingStatus($input: OnboardingStatusInput!) {
                aipmOnboardingStatus(input: $input) {
                  funnelState
                  completedAt
                }
              }
            `,
              variables: {
                input: { programId }
              }
            })
          }
        );

        const data = await response.json();
        return data?.data?.aipmOnboardingStatus || null;
      } catch (error) {
        console.error('[App] Error fetching onboarding status:', error);
        return null;
      }
    };

    /**
     * Check if user has completed checkout for a program
     * Retroactive onboarding should only trigger AFTER checkout is complete
     * Uses the existing getBestStudentInstance query which includes isProgramCheckout
     */
    const checkStudentCheckoutStatus = async (
      userId: string,
      programId: string
    ): Promise<boolean> => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_NEXUS_API || 'http://localhost:3007'}/graphql`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
            },
            body: JSON.stringify({
              query: `
              query GetBestStudentInstance($input: GetStudentInstancesInput!) {
                getBestStudentInstance(input: $input) {
                  id
                  isProgramCheckout
                }
              }
            `,
              variables: {
                input: {
                  participantId: userId,
                  programId: programId
                }
              }
            })
          }
        );

        const data = await response.json();
        const student = data?.data?.getBestStudentInstance;

        // Checkout is complete if isProgramCheckout is true
        const isCheckedOut = student?.isProgramCheckout === true;
        console.log('[App] Student checkout status:', { isCheckedOut, studentId: student?.id });
        return isCheckedOut;
      } catch (error) {
        console.error('[App] Error checking student checkout status:', error);
        return false;
      }
    };

    /**
     * Check and trigger retroactive onboarding automatically
     */
    const checkAndTriggerRetroactiveOnboarding = async (user: any, route: any) => {
      // Emergency kill switch - only blocks if explicitly set to 'true'
      if (FEATURE_FLAGS.AIPM_ONBOARDING_KILL_SWITCH) return;

      // Skip if already checked this session
      if (hasCheckedRetroactiveOnboarding.value) return;

      // Skip if onboarding is already open
      if (aipmOnboarding.state.isOpen) {
        console.log('[App] Onboarding dialog already open, skipping retroactive check');
        return;
      }

      // Must be a program page
      if (!isProgramRoute(route)) {
        console.log('[App] Not a program route, skipping retroactive onboarding');
        return;
      }

      // The retroactive "Support just added" nudge is PARTICIPANT-facing. It must
      // never auto-fire for the organizer/creator who is building or monitoring the
      // program — i.e. the organizer guide (/guide/organizer/:id) or setup mode
      // (?isSetup=true). Those routes still carry a programId, so without this guard
      // the simplified route-based participant check treats the creator as a
      // participant and pops the onboarding during setup.
      const routePath = route?.path || '';
      const inSetupMode = route?.query?.isSetup === 'true' || route?.query?.isSetup === true;
      if (routePath.includes('/guide/organizer/') || inSetupMode) {
        console.log('[App] Organizer/setup context, skipping retroactive participant onboarding');
        return;
      }

      const programId = getProgramIdFromRoute(route);
      if (!programId) {
        console.log('[App] No program ID found in route, skipping retroactive onboarding');
        return;
      }

      // Must have user ID
      const userId = user?._id?.toString();
      if (!userId) {
        console.log('[App] No user ID found, skipping retroactive onboarding');
        return;
      }

      console.log('[App] Checking retroactive onboarding for program:', programId);

      // Mark as checked to prevent repeated checks
      hasCheckedRetroactiveOnboarding.value = true;

      try {
        // Fetch AutoPilot config and check centralized gate
        let autoPilot: any = null;
        try {
          autoPilot = await getAutoPilotByProgramId(programId);
        } catch {
          console.log('[App] No AutoPilot found for program:', programId);
        }

        console.log('[App] AutoPilot found:', {
          hasAutoPilot: !!autoPilot,
          status: autoPilot?.status,
          hasOnboardingConfig: !!autoPilot?.onboardingConfig,
          enabled: autoPilot?.onboardingConfig?.enabled,
          required: autoPilot?.onboardingConfig?.required,
          retroactiveEnabled: autoPilot?.onboardingConfig?.retroactiveEnabled
        });

        // Use centralized gate: AutoPilot status, onboardingConfig, permanent dismissal
        if (!canStartOnboarding(programId, autoPilot, aipmOnboarding.state)) {
          console.log('[App] Blocked by centralized gate, skipping retroactive onboarding', {
            autoPilotStatus: autoPilot?.status,
            hasConfig: !!autoPilot?.onboardingConfig,
            configEnabled: autoPilot?.onboardingConfig?.enabled,
            isComplete: aipmOnboarding.state.isComplete,
            isPermanentlyDismissed: typeof isPermanentlyDismissed === 'function' ? 'check localStorage' : 'N/A'
          });
          return;
        }

        // Per-program retroactive gate: admin must explicitly enable
        // EXCEPTION: required onboarding always re-triggers (handles page refresh mid-onboarding
        // and ensures required programs reach all participants regardless of retroactiveEnabled)
        const isOnboardingRequired = autoPilot?.onboardingConfig?.required === true;
        if (!autoPilot?.onboardingConfig?.retroactiveEnabled && !isOnboardingRequired) {
          console.log('[App] Retroactive onboarding not enabled for this program');
          return;
        }

        // CRITICAL: Check if user has completed checkout first
        // Retroactive onboarding should ONLY trigger AFTER checkout is complete
        const isCheckedOut = await checkStudentCheckoutStatus(userId, programId);
        console.log('[App] Checkout status:', { isCheckedOut, userId, programId });
        if (!isCheckedOut) {
          console.log('[App] User has not completed checkout, skipping retroactive onboarding');
          return;
        }

        // Fetch onboarding status from backend
        const status = await fetchOnboardingStatus(programId);
        console.log('[App] Onboarding status:', status);

        // Check session dismissal (skip for required — required onboarding can't be dismissed)
        if (!isOnboardingRequired && retroactiveOnboarding.isSessionDismissed(programId)) {
          console.log('[App] Session dismissed, skipping retroactive onboarding');
          return;
        }

        // Check if completion predates retroactive enablement
        // When admin toggles retroactive ON, retroactiveEnabledAt is set.
        // If participant completed BEFORE that timestamp, treat as not complete
        // (admin wants to re-trigger for existing participants).
        let effectiveFunnelState = status?.funnelState;
        if (effectiveFunnelState === 'complete' && status?.completedAt) {
          const retroactiveEnabledAt = autoPilot?.onboardingConfig?.retroactiveEnabledAt;
          if (retroactiveEnabledAt && new Date(status.completedAt) < new Date(retroactiveEnabledAt)) {
            console.log('[App] Completion predates retroactive enablement, re-triggering', {
              completedAt: status.completedAt,
              retroactiveEnabledAt
            });
            effectiveFunnelState = 'not_started';
          }
        }

        // Trigger if not started or in progress (not completed)
        if (
          !status ||
          effectiveFunnelState === 'not_started' ||
          effectiveFunnelState === 'in_progress'
        ) {
          console.log('[App] Triggering retroactive onboarding for program:', programId);
          // Small delay to ensure UI is ready
          await nextTick();

          // Apply coordinator settings FIRST (before loadPersonas, which uses defaultPersonaId to filter)
          const onboardingCfg = autoPilot?.onboardingConfig;
          if (onboardingCfg) {
            aipmOnboarding.setCoordinatorSettings({
              skippable: onboardingCfg.required !== true,
              phoneRequired: onboardingCfg.phoneRequired || false,
              customWelcome: onboardingCfg.customWelcome,
              defaultPersonaId: onboardingCfg.defaultPersonaId,
              defaultPersonaIds: onboardingCfg.defaultPersonaIds
            });
          }

          // Restrict the channel picker to the program's configured channels.
          // The retroactive path opens the dialog directly (bypassing startOnboarding,
          // which is where checkout normally applies this), so without this the channel
          // step would leak Email into SMS-only programs.
          aipmOnboarding.applyAutoPilotChannels(autoPilot);

          // Load personas via composable (respects defaultPersonaId from coordinator settings)
          try {
            await aipmOnboarding.loadPersonas();
            console.log('[App] Loaded personas:', aipmOnboarding.state.availablePersonas.length);
          } catch (err) {
            console.warn('[App] Failed to load personas:', err);
          }

          // Narrow channels to the program's enabled channels AND load the coaching
          // policy. This manual retroactive path bypasses startOnboarding, so without
          // this the channel phase shows SMS+Email regardless of config and the
          // coaching phase never appears (policy/scheduling never load). Persona
          // scheduling still loads via the mentor phase's auto-select.
          try {
            await aipmOnboarding.hydrateConfigFromAutoPilot(programId, autoPilot);
          } catch (err) {
            console.warn('[App] Failed to hydrate channels/coaching config:', err);
          }

          // Pre-fill user data from profile (phone, email verification status)
          // Vuex user doesn't include phone data, so fetch it from the User document via REST API
          const currentUser = getUser.value;
          const userId2 = currentUser?._id || currentUser?.id || currentUser?.userId;

          console.log('[App] Pre-filling user data, userId:', userId2, 'currentUser:', currentUser);

          if (userId2) {
            try {
              // Fetch full user data including phone from backend via REST API
              // This endpoint returns the full User document from MongoDB
              const apiUrl = `${
                import.meta.env.VITE_PROGRAM_DOC
              }/document?document_type=User&document_id=${userId2}`;
              console.log('[App] Fetching user data from:', apiUrl);

              const userDataResponse = await fetch(apiUrl, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
                }
              });

              console.log('[App] User data response status:', userDataResponse.status);
              const userDataResult = await userDataResponse.json();
              console.log('[App] User data raw result:', userDataResult);

              // The REST API returns data in { data: { ...userData } } format
              const userData = userDataResult?.data || userDataResult;

              console.log('[App] Fetched user data for pre-fill:', {
                phoneNumber: userData?.phoneNumber,
                phoneVerified: userData?.phoneVerified,
                email: userData?.email
              });

              if (userData && (userData.phoneNumber || userData.phoneVerified)) {
                aipmOnboarding.prefillUserData({
                  phoneNumber: userData.phoneNumber,
                  phoneVerified: userData.phoneVerified || false,
                  email: userData.email,
                  emailVerified: userData.emailVerified !== false
                });
              } else {
                console.log('[App] No phone data in user document');
              }
            } catch (err) {
              console.warn('[App] Failed to fetch user data for pre-fill:', err);
              if (currentUser) {
                aipmOnboarding.prefillUserData({
                  phoneNumber: currentUser.phoneNumber,
                  phoneVerified: currentUser.phoneVerified || false,
                  email: currentUser.email,
                  emailVerified: currentUser.emailVerified || true
                });
              }
            }
          } else {
            console.log('[App] No userId found for pre-fill');
          }

          // Set the onboarding state directly (bypasses Apollo client requirement)
          aipmOnboarding.state.programId = programId;
          aipmOnboarding.state.entryPoint = 'retroactive';
          aipmOnboarding.state.currentPhase = 'welcome';
          aipmOnboarding.state.isOpen = true;
          aipmOnboarding.state.error = null;
          console.log('[App] Onboarding dialog opened for retroactive flow');
        } else {
          console.log('[App] Retroactive onboarding not needed - already complete');
        }
      } catch (error) {
        console.error('[App] Error checking retroactive onboarding:', error);
      }
    };

    // Watch for user + route + profile completeness changes to trigger retroactive onboarding
    // CRITICAL: isProfileComplete must be in watch sources because it resolves asynchronously
    // after login (via IS_PORTFOLIO_COMPLETE_QUERY). Without it, the watcher fires when
    // getUser changes but isProfileComplete is still false, and never retries.
    // immediate: true ensures it fires on creation (handles page refresh where all values
    // are already settled from storage rehydration and the watcher would never trigger).
    watch(
      () => [getUser.value, instance?.proxy?.$route, isProfileComplete.value] as const,
      async ([user, route, profileComplete]) => {
        // Must be authenticated with complete profile
        if (!user || !profileComplete) return;

        // Wait a tick for route to settle
        await nextTick();

        // Check and trigger if needed
        checkAndTriggerRetroactiveOnboarding(user, route);
      },
      { immediate: true, deep: true }
    );

    // Also check on mount after initial load
    onMounted(async () => {
      console.log('[App] onMounted - checking retroactive onboarding');
      // Wait for user state to be ready
      await nextTick();
      setTimeout(() => {
        const user = getUser.value;
        const route = instance?.proxy?.$route;
        console.log('[App] Delayed check:', {
          hasUser: !!user,
          isProfileComplete: isProfileComplete.value,
          routeName: route?.name,
          routePath: route?.path,
          routeParams: route?.params,
          isProgramRoute: isProgramRoute(route)
        });
        if (user && isProfileComplete.value && isProgramRoute(route)) {
          checkAndTriggerRetroactiveOnboarding(user, route);
        } else {
          console.log('[App] Conditions not met for retroactive onboarding:', {
            hasUser: !!user,
            isProfileComplete: isProfileComplete.value,
            isProgramRoute: isProgramRoute(route)
          });
        }
      }, 1000); // 1s delay to ensure everything is loaded
    });

    // if (import.meta.env.VITE_NODE_ENV === 'production' && window.$chatwoot && getUser) {
    //   // FIXME: Enable HMAC validation
    //   const computedValue = getUser.value;
    //   if (computedValue) {
    //     window.$chatwoot?.setUser(computedValue._id, {
    //       email: computedValue.email,
    //       name: computedValue.firstName + computedValue.lastName,
    //       phone_number: computedValue.phoneNumber
    //     });
    //   }
    // }
    const { getLinearLoading: loading } = useToolGetters(['getLinearLoading']);
    const password = ref('');
    const show1 = ref(false);
    const isResetting = ref(false);

    /* NO LAYOUT GUESSING before the router resolves. On a cold load $route is
       still the placeholder (no meta), so `|| 'default'` flashed the dark
       navbar over every no-nav page (/connect, /event/*) for a beat before
       flipping (Derick 2026-09-01). Until the first navigation resolves we
       render a neutral boot screen instead; a safety timer keeps the app
       from blanking forever if onReady never fires. */
    const routerReady = ref(false);
    try {
      instance?.proxy?.$router?.onReady(
        () => { routerReady.value = true; },
        () => { routerReady.value = true; }
      );
    } catch { routerReady.value = true; }
    setTimeout(() => { routerReady.value = true; }, 4000);
    const layout = computed(() => {
      if (!routerReady.value) return null;
      return `${instance?.proxy?.$route?.meta?.layout || 'default'}-layout`;
    });

    // Sitewide announcement banner
    const bannerMessage = import.meta.env.VITE_SITE_BANNER_MESSAGE || '';
    const bannerDismissed = ref(sessionStorage.getItem('site-banner-dismissed') === 'true');
    const showBanner = computed(() => !!bannerMessage && !bannerDismissed.value);
    const dismissBanner = () => {
      bannerDismissed.value = true;
      sessionStorage.setItem('site-banner-dismissed', 'true');
    };

    // Only show AI Creator Shell on creator-facing routes
    const AI_SHELL_ROUTES = new Set([
      'create',
      'createProgram',
      'activitySchemaBuilder',
      'activitySchemaBuilderEdit',
      'activitySchemaBuilderLegacy',
      'guide/organizer',
      'CommsInbox'
    ]);
    const showAiCreatorShell = computed(() => {
      const routeName = instance?.proxy?.$route?.name;
      return routeName ? AI_SHELL_ROUTES.has(routeName as string) : false;
    });

    return {
      deferredDialogs,
      layout,
      loading,
      isDialogOpen,
      password,
      show1,
      isResetting,
      // AIPM Retroactive Onboarding handlers
      handleRetroactiveOnboardingComplete,
      handleRetroactiveOnboardingDismiss,
      // Sitewide banner
      bannerMessage,
      showBanner,
      dismissBanner,
      showAiCreatorShell
    };
  }
  // provide: {

  // },
});
</script>
<style lang="scss" scoped>
.timerchip {
  &__container {
    width: 100% !important;
    max-width: 700px !important;
  }
}
.balloon-button-container {
  position: fixed;
  top: 70px;
  right: 30px;
  z-index: 9999;
}

.close-icon {
  position: absolute !important;
  top: 5px;
  right: 5px;
  border-radius: 50%;
  cursor: pointer;
  color: red !important;
}

/* Neutral boot screen while the router resolves the first navigation -
   no layout is guessed, so no navbar flash on no-nav pages. */
.app-boot {
  position: fixed;
  inset: 0;
  background: #faf7f0;
}
</style>
